import { useQuery } from '@apollo/client';
import { useCallback, useMemo } from 'react';

import { ALL_CUSTOM_SKILL_FILTERS, isBuiltInSkillCommand, sortCustomSkills } from './custom-skill-model';
import { buildSkillCommands, groupSkillCommandsByCategory } from '../../../../lib/skill-constants';
import { GET_CUSTOM_SKILL_COLLECTIONS } from '../../../../queries/chats';
import { useSkillsSafe } from '../../contexts/skills-context';

export function useCustomSkillCollections({
  accountUuid,
  userUuid,
  showMySkills,
  showSharedSkills,
}: {
  accountUuid?: string;
  userUuid: string;
  showMySkills: boolean;
  showSharedSkills: boolean;
}) {
  const skillsContext = useSkillsSafe();
  const {
    data: customSkillCollectionsData,
    loading: areCustomSkillCollectionsLoading,
    error: customSkillCollectionsError,
    refetch: refetchCustomSkillCollections,
  } = useQuery(GET_CUSTOM_SKILL_COLLECTIONS, {
    variables: {
      ...(accountUuid ? { accountUuid } : {}),
      userUuid,
      customSkillFilters: ALL_CUSTOM_SKILL_FILTERS,
      sharedCustomSkillFilters: {
        userUuid,
        enabled: [true, false],
      },
      includeCustomSkills: showMySkills,
      includeSharedCustomSkills: showSharedSkills,
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  const mySkills = useMemo(
    () => (showMySkills ? sortCustomSkills(customSkillCollectionsData?.customSkills ?? []) : []),
    [showMySkills, customSkillCollectionsData?.customSkills],
  );
  const sharedSkills = useMemo(
    () => (showSharedSkills ? sortCustomSkills(customSkillCollectionsData?.sharedCustomSkills ?? []) : []),
    [showSharedSkills, customSkillCollectionsData?.sharedCustomSkills],
  );
  const skills = useMemo(() => [...mySkills, ...sharedSkills], [mySkills, sharedSkills]);
  const runtimeSkills = useMemo(
    () => customSkillCollectionsData?.runtimeSkills ?? [],
    [customSkillCollectionsData?.runtimeSkills],
  );
  const runtimeSkillCommands = useMemo(() => buildSkillCommands(runtimeSkills), [runtimeSkills]);
  const wtwSkillCommands = useMemo(() => runtimeSkillCommands.filter(isBuiltInSkillCommand), [runtimeSkillCommands]);
  const groupedWtwSkillCommands = useMemo(() => groupSkillCommandsByCategory(wtwSkillCommands), [wtwSkillCommands]);
  const runtimeSkillsByUuid = useMemo(
    () => new Map(runtimeSkills.map((skill) => [skill.id, skill] as const)),
    [runtimeSkills],
  );
  const isInitialLoading = areCustomSkillCollectionsLoading && !customSkillCollectionsData;
  const error = customSkillCollectionsError;

  const reloadSkills = useCallback(async () => {
    await refetchCustomSkillCollections({
      ...(accountUuid ? { accountUuid } : {}),
      userUuid,
      customSkillFilters: ALL_CUSTOM_SKILL_FILTERS,
      sharedCustomSkillFilters: {
        userUuid,
        enabled: [true, false],
      },
      includeCustomSkills: showMySkills,
      includeSharedCustomSkills: showSharedSkills,
    });
  }, [accountUuid, refetchCustomSkillCollections, showMySkills, showSharedSkills, userUuid]);

  const refreshAllSkills = useCallback(async () => {
    await Promise.all([reloadSkills(), skillsContext?.refetchSkills()]);
  }, [reloadSkills, skillsContext]);

  return {
    error,
    groupedWtwSkillCommands,
    isInitialLoading,
    mySkills,
    refreshAllSkills,
    reloadSkills,
    runtimeSkillsByUuid,
    sharedSkills,
    skills,
    wtwSkillCommands,
  };
}
